import {GridFilterModel, GridSortModel, useGridApiRef} from '@mui/x-data-grid';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ArrMenuHandle, CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import {t} from 'i18next';
import {ToastType} from '../../../shared/constants';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  IndexStatus,
  setCreateCustomIndex,
  setIndexStatus,
  setIsReadyOnlyView,
  setSelectedMetadataFieldNames,
  setViewIndexId,
} from '../../../redux/slice/createCustomIndexSlice';
import {
  useFetchCustomIndexDetailsById,
  useGetIndexManagementData,
  useDeleteCustomIndex,
  useRebuildCustomIndex,
} from '../../../hooks/ingestions/useIngestions';
import {
  IndexManagementResponse,
  IndexManagementData as IndexManagementItemType,
} from '../../../api/types/ingestionApi.type';
import dayjs from 'dayjs';
import {useQueryClient} from '@tanstack/react-query';

const CUSTOM_INDEX_LIST_STATUS_UPDATED = 'CustomIndexListStatusUpdated';

export const UseIndexManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lastEvent = useSelector((state: RootState) => state.signalr?.lastEvent);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    setPageNumber(1);
  }, []);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'createDate',
      sort: 'desc',
    },
  ]);
  const [isDeleteCustomIndex, setIsDeleteCustomIndex] =
    useState<boolean>(false);
  const [rebuildModalOpen, setRebuildModalOpen] = useState<boolean>(false);
  const [clickedMenuRow, setClickedMenuRow] =
    useState<IndexManagementItemType | null>(null);
  const [selectedCustomIndexId, setSelectedCustomIndexId] = useState<
    number | undefined
  >(undefined);
  const [indexManagementData, setIndexManagementData] =
    useState<IndexManagementResponse>({
      items: [],
      totalRecords: 0,
      page: 1,
      pageSize: 25,
    });
  const [pinnedColumn, setPinnedColumn] = useState<IndexManagementItemType[]>(
    []
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const payload = useMemo(() => {
    const pascalCaseSort =
      sortModel[0]?.field?.charAt(0)?.toUpperCase() +
      sortModel[0]?.field?.slice(1);
    return {
      Page: pageNumber,
      PageSize: pageSize,
      SortBy: pascalCaseSort || 'CreatedDate',
      SortDirection: sortModel[0]?.sort === 'asc' ? 'ASC' : 'DESC',
      Filters: filterModel.items?.map((data) => {
        return {
          Field: data.field,
          Operator: 'Contains',
          Value:
            data.field === 'createDate' || data.field === 'lastIndexDate'
              ? dayjs(data.value).format('YYYY-MM-DD')
              : data.value,
        };
      }),
    };
  }, [pageNumber, sortModel, pageSize, filterModel]);
  const getUTCFormat = (utcTime: string) => {
    return dayjs.utc(utcTime).format('YYYY/MM/DD HH:mm:ss');
  };
  const {data: indexManagementResponse, isLoading: indexManagementLoading} =
    useGetIndexManagementData(payload);
  const {
    data: customIndexDetailsById,
    isLoading: fetchIdLoading,
    isError,
    isSuccess,
  } = useFetchCustomIndexDetailsById(selectedCustomIndexId, {
    enabled: selectedCustomIndexId !== undefined,
  });

  useEffect(() => {
    if (indexManagementLoading || !indexManagementResponse) return;
    const {nullIndexData, validData} = indexManagementResponse.items?.reduce(
      (acc, item) => {
        const updatedItem = {
          ...item,
          createDate: getUTCFormat(item.createDate),
          lastIndexDate:
            getUTCFormat(item.lastIndexDate) !== 'Invalid Date'
              ? getUTCFormat(item.lastIndexDate)
              : '---',
        };

        if (!item.customIndexID) {
          acc.nullIndexData.push(updatedItem);
        } else {
          acc.validData.push(updatedItem);
        }
        return acc;
      },
      {
        nullIndexData: [] as IndexManagementItemType[],
        validData: [] as IndexManagementItemType[],
      }
    ) ?? {
      nullIndexData: [],
      validData: [],
    };
    setPinnedColumn(nullIndexData);

    setIndexManagementData({...indexManagementResponse, items: validData});
  }, [indexManagementResponse, indexManagementLoading]);

  useEffect(() => {
    if (lastEvent?.payload?.type !== CUSTOM_INDEX_LIST_STATUS_UPDATED) return;

    const indexUpdate = lastEvent?.payload?.payload;
    const customIndexId = Number(indexUpdate?.customIndexId);
    const nextStatus = indexUpdate?.status;
    if (
      indexUpdate?.customIndexId == null ||
      !Number.isFinite(customIndexId) ||
      nextStatus == null
    ) {
      return;
    }

    setIndexManagementData((previous) => {
      const isMatched = previous?.items?.some(
        (item) => Number(item?.customIndexID) === customIndexId
      );
      if (!isMatched) return previous;

      return {
        ...previous,
        items: previous.items.map((item) =>
          Number(item?.customIndexID) === customIndexId
            ? {...item, status: nextStatus}
            : item
        ),
      };
    });

    setClickedMenuRow((previous) =>
      previous && Number(previous.customIndexID) === customIndexId
        ? {...previous, status: nextStatus}
        : previous
    );
  }, [lastEvent]);

  useEffect(() => {
    if (fetchIdLoading) {
      return;
    }
    if (isError) {
      setIsReadyOnlyView(false);
      triggerToast(
        t('ingestions.indexManagement.viewCustomIndex.viewIndexErrorMsg'),
        [ToastType.Error]
      );
      return;
    }
    if (isSuccess && customIndexDetailsById) {
      dispatch(
        setCreateCustomIndex({
          indexName: customIndexDetailsById.indexName,
          securityGroupIds: customIndexDetailsById.securityGroupIds,
          savedSearchId: customIndexDetailsById.savedSearchId,
          fields: customIndexDetailsById.fields.map((field) => ({
            fieldId: field.parentFieldID ? field.parentFieldID : field.fieldId,
            analyzerId: field.analyzerId,
          })),
          notificationEmails: customIndexDetailsById.notificationEmails,
        })
      );
      dispatch(
        setSelectedMetadataFieldNames(
          customIndexDetailsById.fields.map((field) => ({
            fieldId: field.parentFieldID ? field.parentFieldID : field.fieldId,
            fieldName: '',
            analyzerKey: '',
            analyzerName: field.analyzerName,
            isDefault: false,
          }))
        )
      );
      navigate('/Ingestion/Analytics/Indexing/CreateCustomIndex');
    }
  }, [
    fetchIdLoading,
    isError,
    isSuccess,
    customIndexDetailsById,
    dispatch,
    navigate,
  ]);

  const actionMenuItem: CustomMenuItem[] = [
    {label: t('ingestions.indexManagement.actionMenu.view'), id: 'view'},
    {
      label: t('ingestions.indexManagement.actionMenu.rebuildIndex'),
      id: 'rebuildIndex',
    },
    {
      label: t('ingestions.indexManagement.actionMenu.deleteIndex'),
      id: 'deleteIndex',
    },
  ];
  const actionMenuRef = useRef<ArrMenuHandle | null>(null);

  const apiRef = useGridApiRef();

  React.useEffect(() => {
    return apiRef.current?.subscribeEvent('rowExpansionChange', (params) => {
      if (params.childrenExpanded) {
        apiRef.current?.autosizeColumns({includeOutliers: true});
      }
    });
  }, [apiRef]);

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    data: IndexManagementItemType
  ) => {
    setClickedMenuRow(data);
    dispatch(setIndexStatus(data.status as IndexStatus));
    actionMenuRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleViewIndex = useCallback(
    (row: IndexManagementItemType) => {
      dispatch(setIsReadyOnlyView(true));
      dispatch(setIndexStatus(row.status as IndexStatus));
      dispatch(setViewIndexId(row.customIndexID ?? 0));
      if (!row.customIndexID) {
        navigate('/Ingestion/Analytics/Indexing/StandardIndex');
        return;
      }
      setSelectedCustomIndexId(row.customIndexID);
    },
    [dispatch, navigate]
  );

  const onMenuItemClick = useCallback(
    (item: {id: string}) => {
      actionMenuRef.current?.close();
      if (item?.id === 'deleteIndex') {
        setIsDeleteCustomIndex(true);
      }

      if (item.id === 'view') {
        if (clickedMenuRow) {
          handleViewIndex(clickedMenuRow);
        } else {
          dispatch(setIsReadyOnlyView(true));
        }
      }

      if (item.id === 'rebuildIndex') {
        setRebuildModalOpen(true);
      }
    },
    [clickedMenuRow, dispatch, handleViewIndex]
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const queryClient = useQueryClient();

  const deleteCustomIndexMutation = useDeleteCustomIndex();
  const rebuildCustomIndexMutation = useRebuildCustomIndex();

  const handleDeleteIndex = async (indexId: number) => {
    try {
      const response = await deleteCustomIndexMutation.mutateAsync(indexId);

      await queryClient.invalidateQueries({queryKey: ['getIndexManagement']});

      setIsDeleteCustomIndex(false);

      triggerToast(
        t(
          'ingestions.indexManagement.deleteCustomIndex.deleteIndexSuccessMsg',
          {
            indexName: clickedMenuRow?.indexName,
          }
        ),
        [ToastType.Success]
      );
    } catch (error) {
      triggerToast(
        t('ingestions.indexManagement.deleteCustomIndex.deleteIndexErrorMsg'),
        [ToastType.Error]
      );
    }
  };

  const handleRebuildIndex = async (indexId: number) => {
    try {
      const response = await rebuildCustomIndexMutation.mutateAsync(indexId);
      await queryClient.invalidateQueries({queryKey: ['getIndexManagement']});
      setRebuildModalOpen(false);

      triggerToast(
        t(
          'ingestions.indexManagement.rebuildCustomIndex.rebuildIndexSuccessMsg',
          {
            indexName: clickedMenuRow?.indexName,
          }
        ),
        [ToastType.Success]
      );
    } catch (error) {
      triggerToast(
        t('ingestions.indexManagement.rebuildCustomIndex.rebuildIndexErrorMsg'),
        [ToastType.Error]
      );
    }
  };

  return {
    filterModel,
    setFilterModel,
    handleFilterModelChange,
    sortModel,
    handleSortModelChange,
    onMenuItemClick,
    handleMoreIconClick,
    handleViewIndex,
    pageNumber,
    pageSize,
    setPageSize,
    setPageNumber,
    actionMenuRef,
    isDeleteCustomIndex,
    setIsDeleteCustomIndex,
    rebuildModalOpen,
    setRebuildModalOpen,
    clickedMenuRow,
    showToast,
    setShowToast,
    toastMessage,
    toastStatus,
    handleDeleteIndex,
    handleRebuildIndex,
    actionMenuItem,
    indexManagementLoading,
    fetchIdLoading,
    indexManagementData,
    pinnedColumn,
  };
};
